import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Backdrop,
  CircularProgress,
  Box,
} from "@mui/material";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";
import {
  errorNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import { useStaffLayout } from "../../Components/Context/CandidateLayoutContext";

export default function Roleprivileges() {
  const { triggerSomethingFromLayout } = useStaffLayout();

  const [selectedRole, setSelectedRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [modulesData, setModulesData] = useState([]);
  const [privilegeState, setprivilegeState] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  // Fetch roles on mount
  useEffect(() => {
    getEmployeeRoleList();
  }, []);

  // Fetch privileges when role changes
  useEffect(() => {
    if (selectedRole) {
      getprivilegesList();
    }
  }, [selectedRole]);

  const getEmployeeRoleList = async () => {
    try {
      const res = await axiosAPI.get(`/UserRole/list/active`);
      setRoles(res?.data?.data || []);

      if (res?.data?.data?.length > 0) {
        setSelectedRole(res?.data?.data[0]?.RL_Id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getprivilegesList = async () => {
    try {
      const res = await axiosAPI.get(`privilege/list/${selectedRole}`);

      const data = res?.data?.data || [];
      setModulesData(data);
      setprivilegeState(generateprivilegeState(data));
    } catch (err) {
      console.error(err);
    }
  };

  const generateprivilegeState = (data) => {
    const result = {};
    data.forEach((mod) => {
      result[mod.subModuleName] = {};
      mod.privileges.forEach((priv) => {
        result[mod.subModuleName][priv.privilegeName] = priv.assigned;
      });
    });
    return result;
  };

  const handleChange = (moduleName, privilegeName) => {
    setprivilegeState((prev) => ({
      ...prev,
      [moduleName]: {
        ...prev[moduleName],
        [privilegeName]: !prev[moduleName][privilegeName],
      },
    }));
  };

  const isModuleAllChecked = (moduleName) => {
    return Object.values(privilegeState[moduleName] || {}).every(Boolean);
  };

  const handleModuleSelectAll = (moduleName) => {
    const allChecked = isModuleAllChecked(moduleName);
    const updated = {};
    Object.keys(privilegeState[moduleName]).forEach((key) => {
      updated[key] = !allChecked;
    });

    setprivilegeState((prev) => ({
      ...prev,
      [moduleName]: updated,
    }));
  };

  const handleSaveprivileges = async () => {
    if (!selectedRole) return;
    setIsDisabled(true);

    const payload = {
      roleId: selectedRole,
      subModules: modulesData.map((module) => {
        const selectedPrivIds = module.privileges
          .filter(
            (priv) =>
              privilegeState?.[module.subModuleName]?.[priv.privilegeName]
          )
          .map((priv) => priv.privilegeId);

        return {
          subModuleId: module.subModuleId,
          privileges: selectedPrivIds, // can be []
        };
      }),
    };

    try {
      const res = await axiosAPI.post("/privilege/assign", payload);
      if (res?.data?.success) {
        successNotify(res?.data?.message);
        triggerSomethingFromLayout();
      } else {
        errorNotify(res?.data?.message);
      }
    } catch (err) {
      console.error(err);
      errorNotify("Something Went Wrong!");
    } finally {
      setIsDisabled(false);
    }
  };

  const excludedPrivilegesMap = {
  "Employee Management": [ "Delete", "Export"],
  "Client Management": [ "Delete", "Export"],
  "Candidate Management": [ "Delete", "Export"],
  "Job Management": [ "Delete", "Export"],
  "Recruitment Procedure": [ "Delete", "Export"],
  "Recruitment Procedure Mapping": ["View", "Delete", "Export", "Edit"],
  "Document Checklist": ["View", "Delete", "Export"],
  "Roles": ["View", "Delete", "Export"],
  "Audit Log": ["Add", "Delete", "Edit"],
  "Dashboard": ["Add", "Delete", "Export", "Edit"],
  "Reports":["Add", "Delete", "Export", "Edit"],
  "Settings":["Add", "Delete", "Export"],

  // Grouped submodules
  "Profession": ["View", "Delete", "Export"],
  "Qualification": ["View", "Delete", "Export"],
  "Designation": ["View", "Delete", "Export"],
  "Company Type": ["View", "Delete", "Export"],
  "Employment Type": ["View", "Delete", "Export"],
  "Document Type": ["View", "Delete", "Export"],
  "Recruitment Country": ["View", "Delete", "Export"],
  "Branch": ["View", "Delete", "Export"],
  "Department": ["View", "Delete", "Export"],
  "Additional Requirement": ["View", "Delete", "Export"],
  "Salary Pay": ["View", "Delete", "Export"],
  "Year Of Experience": ["View", "Delete", "Export"],
  
};

const shouldExcludePrivilege = (subModuleName, privilegeName) => {
  const excluded = excludedPrivilegesMap[subModuleName];
  return excluded?.includes(privilegeName);
};


  return (
     <Paper
          sx={{
            maxWidth: `100vw`,
            padding: 2,
            margin: "auto",
            boxShadow: "none",
            overflowX: "auto",
            fontFamily: "Geist, sans-serif",
            minHeight: "100%",
          }}
        >
      {/* Main Heading ================================================================================================================================================================== */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          textAlign: "left",
          marginBottom: 3,
          fontFamily: "Geist, sans-serif",
        }}
      >
        Privileges Mapping
      </Typography>

      <Grid container spacing={2} xs={12}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={4}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Role <span style={{ color: "red" }}> * </span>
          </Typography>
          <SelectInput
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            options={roles?.map((item) => ({
              value: item?.RL_Id,
              label: item?.RL_Name,
            }))}
            placeholder="Select Role"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} my={1}>
        {modulesData?.length
          ? modulesData?.map((module) => (
              <Grid
                item
                sx={{ padding: "5px" }}
                xs={12}
                sm={12}
                md={6}
                lg={4}
                xl={3}
              >
                <Paper
                  sx={{
                    p: 2.5,
                    border: "1px solid grey",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '20px',
                      fontFamily: "Geist, sans-serif",
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {module.subModuleName}
                  </Typography>

                  <Grid container >
                    {module.privileges
                      .filter((priv) => !shouldExcludePrivilege(module.subModuleName, priv.privilegeName))
                      .map((priv) => (
                      <Grid item xs={12} sm={6} key={priv.privilegeId}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                privilegeState?.[module.subModuleName]?.[
                                  priv.privilegeName
                                ] || false
                              }
                              onChange={() =>
                                handleChange(
                                  module.subModuleName,
                                  priv.privilegeName
                                )
                              }
                              sx={{ transform: "scale(1.2)", padding: "10px" }}
                            />
                          }
                          label={priv.privilegeName}
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontFamily: "Geist, sans-serif",
                              fontWeight: 500,
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Box
                    sx={{
                      borderTop: "1px solid #e0e0e0",
                      display: "flex",
                      // justifyContent: "flex-end",
                    }}
                    mt={1}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isModuleAllChecked(module.subModuleName)}
                          onChange={() =>
                            handleModuleSelectAll(module.subModuleName)
                          }
                          sx={{ transform: "scale(1.2)", padding: "10px" }}
                        />
                      }
                      label="Select All"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontFamily: "Geist, sans-serif",
                          fontWeight: 500,
                          fontSize: "1rem",
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            ))
          : null}
      </Grid>

      {/* Buttons */}
      {modulesData?.length ? (
        <Grid
          container
          spacing={2}
          justifyContent={{ xs: "center", sm: "flex-end" }}
          sx={{ mt: 3 }}
        >
          <Grid item>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={handleSaveprivileges}
              disabled={isDisabled}
            >
              {isDisabled ? "Saving..." : "Save"}
            </CommonButton>
          </Grid>
        </Grid>
      ) : null}

      <Backdrop
        open={loaderOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
}
